import type { AuthUser, Settings } from '@mendikot/app-core';
import { paths } from '@mendikot/protocol';
import { LEGAL } from '../legal';
import { loadOnline } from './firebase';

/**
 * Proof of notice and consent (DPDP Act s.6(10)): which notice version the player accepted, that they
 * confirmed they are 18+, and their analytics choice. Rewritten whenever the choice changes.
 */
export async function recordConsent(uid: string, analytics: boolean, lang: string): Promise<void> {
  const [{ db }, { ref, serverTimestamp, set }] = await Promise.all([
    loadOnline(),
    import('firebase/database'),
  ]);
  await set(ref(db, paths.userConsent(uid)), {
    noticeVersion: LEGAL.noticeVersion,
    ageConfirmed: true,
    analytics,
    lang,
    at: serverTimestamp(),
  });
}

/**
 * Right to access (DPDP Act s.11): everything stored about the player — account, profile, stats,
 * consent record, notification tokens, match history — plus this device's settings, as a JSON file.
 */
export async function downloadMyData(user: AuthUser, settings: Settings): Promise<void> {
  const [{ db }, { get, ref }] = await Promise.all([loadOnline(), import('firebase/database')]);
  const [account, history] = await Promise.all([
    get(ref(db, paths.user(user.uid))),
    get(ref(db, paths.history(user.uid))),
  ]);
  const data = {
    exportedAt: new Date().toISOString(),
    dataFiduciary: LEGAL.fiduciary,
    contact: LEGAL.contactEmail,
    account: {
      id: user.uid,
      guest: user.isAnonymous,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
      signInMethods: user.providers,
    },
    stored: (account.val() as unknown) ?? {},
    matchHistory: (history.val() as unknown) ?? {},
    thisDevice: settings,
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `mendikot-my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
